<template>
  <div>
    <PageAllow controller="StockDetails" action="index" />
    <Breadcrumbs :items="items" />
    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <!-- 入库日期 / 时间类型 -->
              <SearcherDatetimeRangeWithRadio
                v-model="options"
                type="datetime"
                show-radios
                :radios="datetimeRadios"
                removeIcon
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <!-- 导出 -->
              <v-btn
                v-if="
                  $permission.permissionAllow(
                    'StockDetails',
                    'positionExport'
                  )
                "
                small
                color="print"
                dark
                class="ms-1"
                @click="positionExport()"
              >
                <v-icon x-small class="pe-1">fa-file-export</v-icon>
                {{ $t("CurrentPosition.Export.stock_position_batch_out") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <!-- 批次号 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="stock_batch_code"
                    v-model="options.stock_batch_code"
                    @text="orderCodeKeyword = $event"
                    :label="$t('StockDetails.Fields.stock_batch_code')"
                  />
                </v-col>
                <!-- 入库单号 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="stock_in_order_code"
                    v-model="options.stock_in_order_code"
                    @text="stockInOrderCodeKeyword = $event"
                    :label="$t('StockDetails.Fields.stock_in_order_code')"
                  />
                </v-col>
                <!-- 商品分类 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherMaterialCategory
                    v-model="materialCategorySelected"
                  />
                </v-col>
                <!-- 商品 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherMaterial
                    v-model="materialSelected"
                    :material-categories="
                      materialCategorySelected ? [materialCategorySelected] : []
                    "
                  />
                </v-col>
                <!-- 仓库中心 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherStock
                    v-model="stockSelected"
                    :stock-types="[0]"
                    :wmsSetting="[1]"
                  />
                </v-col>
                <!-- 库位 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherStockPosition
                    v-model="positionSelected"
                    :stockId="stockSelected ? stockSelected.id : 0"
                    :defaultId="$route.query.stock_position_id ? Number($route.query.stock_position_id) : null"
                    :stockAreaSetting="
                      stockSelected && stockSelected.stock_area ? true : false
                    "
                  />
                </v-col>
                <!-- 状态 -->
                <v-col cols="12" sm="6" md="4">
                  <!-- 过期 -->
                  <SearcherExpiredStatus
                    :items="$consts.expiredFlgs"
                    v-model="options.expired_flgs"
                  />
                </v-col>
                <v-spacer />
                <v-col cols="12" sm="6" md="4">
                  <!-- 冻结 -->
                  <SearcherStatus
                    :label="$t('StockDetails.prohibit_flg.ObjectName')"
                    :items="$consts.prohibitFlgs"
                    v-model="options.prohibit_flgs"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <!-- 使用 -->
                  <SearcherStatus
                    :items="$consts.use.use_status.filter(
                      (element) => element.value != 1
                    )"
                    v-model="options.status"
                    :label="$t('StockDetails.use_status.ObjectName')"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <!-- 表格 -->
        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="stock_detail"
            :options.sync="options"
            :loading="loading"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            multi-sort
            class="elevation-1 px-4 pt-1 sticky-table"
            id="sticky-table"
            :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
            fixed-header
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:body.prepend>
              <tr class="d-none d-sm-table-row">
                <td>
                  <span>{{ $t("TableSum") }}</span>
                </td>
                <td colspan="4"></td>
                <td :align="$vuetify.rtl ? 'left' : 'right'">
                  <span>{{ stock_first_total | formatTwoDot }}</span>
                </td>
                <td :align="$vuetify.rtl ? 'left' : 'right'">
                  <span> {{ stock_current_total | formatTwoDot }}</span>
                </td>
                <td :align="$vuetify.rtl ? 'left' : 'right'">
                  <span
                    >{{ $t("Global.money")
                    }}{{ price_total | formatMoney }}</span
                  >
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="settings && settings.product_date_setting"></td>
                <td v-if="settings && settings.product_date_setting"></td>
              </tr>
              <!-- 小屏幕 -->
              <tr class="d-table-row d-sm-none">
                <td class="v-data-table__mobile-row">
                  <div class="v-data-table__mobile-row__header">
                    {{ $t("TableSum") }}
                  </div>
                </td>
                <td class="v-data-table__mobile-row">
                  <div class="v-data-table__mobile-row__header">
                    {{ $t("StockDetails.Fields.stock_first") }}
                  </div>
                  <div class="v-data-table__mobile-row__cell">
                    {{ stock_first_total | formatTwoDot }}
                  </div>
                </td>
                <td class="v-data-table__mobile-row">
                  <div class="v-data-table__mobile-row__header">
                    {{ $t("StockDetails.Fields.stock_current") }}
                  </div>
                  <div class="v-data-table__mobile-row__cell">
                    {{ $t("Global.money")
                    }}{{ stock_current_total | formatMoney }}
                  </div>
                </td>
                <td class="v-data-table__mobile-row">
                  <div class="v-data-table__mobile-row__header">
                    {{ $t("StockDetails.Fields.count_num") }}
                  </div>
                  <div class="v-data-table__mobile-row__cell">
                    {{ $t("Global.money") }}{{ price_total | formatMoney }}
                  </div>
                </td>
              </tr>
            </template>

            <template v-slot:header.actions="{ header }">
              <span class="ml-3">
                {{ header.text }}
                <v-icon x-small>fa-sliders-h</v-icon>
              </span>
            </template>
            <template v-slot:item.material.material_name="{ item }">
              {{
                $t(
                  settings
                    ? settings.material_name_display_type == 4
                      ? "Material.nameWithCodeAndStandard"
                      : settings.material_name_display_type == 3
                      ? "Material.materialNameWithCodeAndStandard"
                      : settings.material_name_display_type == 2
                      ? "Material.materialNameWithCode"
                      : "Material.materialName"
                    : "Material.materialName",
                  [
                    item.material.material_code,
                    item.material.material_name,
                    item.material.standard_name,
                  ]
                )
              }}
            </template>
            <!-- 状态文字/颜色 -->
            <!-- 冻结状态 -->
            <template v-slot:item.prohibit_flg="{ item, index }">
              <span :v-resize="!index ? $stickTableResizer() : null"> </span>
              <v-chip
                small
                label
                :color="item.prohibit_flg | prohibitFlgsColor"
                :text-color="item.prohibit_flg | prohibitFlgsTextColor"
              >
                {{ item.prohibit_flg | prohibitFlgsText }}
              </v-chip>
            </template>
            <!-- 过期状态 expired_flg-->
            <template v-slot:item.expired_flg="{ item }">
              <v-chip
                small
                label
                :color="item.expired_flg | expiredFlgsColor"
                :text-color="item.expired_flg | expiredFlgsTextColor"
              >
                {{ item.expired_flg | expiredFlgsText }}
              </v-chip>
            </template>
            <!-- 入库量格式化 -->
            <template v-slot:item.stock_first="{ item }">
              {{ item.stock_first | formatTwoDot }}
            </template>
            <!-- 当前库存格式化 -->
            <template v-slot:item.stock_current="{ item }">
              {{ item.stock_current | formatTwoDot }}
            </template>
            <!-- 格式化金额 -->
            <template v-slot:item.price="{ item }">
              {{ $t("Global.money") }}{{ item.price | formatMoney }}
            </template>
            <!-- 小计 -->
            <template v-slot:item.count_num="{ item }">
              {{ $t("Global.money")
              }}{{ (item.price * item.stock_current) | formatMoney }}
            </template>
            <!-- 库位名称 -->
            <template v-slot:item.stock_position.position_name="{ item }">
              <span v-if="item.stock_position">
                {{ item.stock_position.position_code }} / {{ item.stock_position.position_name }}
              </span>
            </template>
            <!-- 生产日期 -->
            <template v-slot:item.production_date="{ item }">
              <span >{{ item.production_date | formatDate }}</span>
            </template>
            <!-- 过期日期 -->
            <template v-slot:item.expired_date="{ item }">
              <span >{{ item.expired_date | formatDate }}</span>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import i18n from "@/i18n";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import StockDetails from "@/api/stock_details";
import SearcherMaterialCategory from "@/components/plugins/SearcherMaterialCategory";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherMaterial from "@/components/plugins/SearcherMaterial";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SearcherExpiredStatus from "@/components/plugins/SearcherExpiredStatus";
import SearcherStockPosition from "@/components/plugins/SearcherStockPosition";


const defaultOptions = {
  datetimeRadioSelected: "in",
  in_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  in_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  expired_time_from: "",
  expired_time_to: "",
  production_time_from: "",
  production_time_to: "",
  material_ids: [],
  category_ids: [],
  stock_batch_code: "",
  status: [0, 2],
  prohibit_flgs: [0, 1],
  expired_flgs: [0, 1],
  stock_in_order_code: "",
  stock_ids: [],
  stock_position_ids: [],
};

export default {
  name: "stockDetails",

  data: () => ({
    stock_detail: [],
    orderCodeKeyword: "",
    materialCategorySelected: null,
    materialSelected: null,
    stockSelected: null,
    positionSelected: null,
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("position_stock_detail-options")
        ? sessionStorage.getItem("position_stock_detail-options")
        : JSON.stringify(defaultOptions)
    ),
    stock_first_total: 0,
    stock_current_total: 0,
    price_total: 0,
    stockInOrderCodeKeyword: "",
  }),

  components: {
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    PageAllow,
    SearcherMaterialCategory,
    SearcherStock,
    SearcherMaterial,
    SearcherKeyword,
    SearcherStatus,
    SearcherExpiredStatus,
    SearcherStockPosition,
  },
  props: {
    detailMode: Boolean,
  },
  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.stockManagement"),
        disabled: false,
        href: "/stocks",
      },
      {
        text: i18n.t("Nav.currentPosition"),
        disabled: false,
        href: "/current-position",
      },
      {
        text: i18n.t("CurrentPosition.edit.stockDetail"),
        disabled: true,
        href: "/position-activities",
      },
    ],
    datetimeRadios: () => [
      {
        key: "in",
        text: i18n.t("StockDetails.Fields.created_date"),
      },
      {
        key: "production",
        text: i18n.t("StockDetails.Fields.production_date"),
      },
      {
        key: "expired",
        text: i18n.t("StockDetails.Fields.expired_date"),
      },
    ],
    headers() {
      let headers = [
        {
          text: i18n.t("StockDetails.Fields.stock_batch_code"),
          align: "start",
          sortable: false,
          value: "stock_batch_code",
        },
        {
          text: i18n.t("StockDetails.Fields.material"),
          align: "start",
          sortable: false,
          value: "material.material_name",
        },
        {
          text: i18n.t("StockDetails.Fields.material_category"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "material.material_category.name",
        },
        {
          text: i18n.t("StockDetails.Fields.unit"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "unit",
        },
        {
          text: i18n.t("StockDetails.Fields.price"),
          align: "end",
          sortable: false,
          value: "price",
        },
        // 入库量
        {
          text: i18n.t("StockDetails.Fields.stock_first"),
          align: "end",
          sortable: false,
          value: "stock_first",
        },
        // 当前库存
        {
          text: i18n.t("StockDetails.Fields.stock_current"),
          align: "end",
          sortable: false,
          value: "stock_current",
        },
        // 小计
        {
          text: i18n.t("StockDetails.Fields.count_num"),
          align: "end",
          sortable: false,
          value: "count_num",
        },
        // 过期状态
        {
          text: i18n.t("StockDetails.Fields.expired_status"),
          align: "start",
          sortable: false,
          value: "expired_flg",
        },
        // 冻结状态
        {
          text: i18n.t("StockDetails.Fields.prohibit_flg"),
          align: "start",
          sortable: false,
          value: "prohibit_flg",
        },
        // 库位名称
        {
          text: i18n.t("StockDetails.Fields.position_name"),
          align: "start",
          sortable: false,
          value: "stock_position.position_name",
        },
        // 库位条码
        {
          text: i18n.t("StockDetails.Fields.bar_code"),
          align: "start",
          sortable: false,
          value: "stock_position.bar_code",
        },
        // 库位标签
        {
          text: i18n.t("StockDetails.Fields.position_label"),
          align: "start",
          sortable: false,
          value: "stock_position.label_name",
        },
      ];
      if (this.settings && this.settings.product_date_setting) {
        headers.push(
          {
            text: i18n.t("StockOrderIN.DetailFields.production_date"),
            align: "center",
            value: "production_date",
            width: 136,
          },
          {
            text: i18n.t("StockOrderIN.DetailFields.expired_date"),
            align: "center",
            value: "expired_date",
            width: 136,
          }
        );
      }
      return headers
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions("position_stock_detail", [
        "materialCategorySelected",
        "materialSelected",
        "stockSelected",
        "positionSelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();
          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "materialCategorySelected":
                this.materialCategorySelected = selected.value;
                break;
              case "materialSelected":
                this.materialSelected = selected.value;
                break;
              case "stockSelected":
                this.stockSelected = selected.value;
                break;
              case "positionSelected":
                this.positionSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },
  methods: {
    onSearch() {
      // 搜索
      this.options = Object.assign({}, this.options, {
        stock_batch_code: this.orderCodeKeyword,
        stock_in_order_code: this.stockInOrderCodeKeyword,

      });
    },
    resetSearch() {
      this.stockSelected = null;
      this.positionSelected = null;
      this.materialCategorySelected = null;
      this.materialSelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.stock_batch_code, this.$refs.stock_in_order_code].forEach((k) => {
        if (k) k.clear();
      });
    },
    getStockDetailsFromAPI() {
      StockDetails.getPositionStockDetails(
        Object.assign(this.options)
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stock_detail = response.data.stock_details;
            this.stock_first_total = response.data.stock_first_total;
            this.stock_current_total = response.data.stock_current_total;
            this.price_total = response.data.price_total;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    exportExcel() {
      StockDetails.exports(this.options);
    },
    positionExport() {
      StockDetails.positionExport(this.options)
    }
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getStockDetailsFromAPI();
        sessionStorage.setItem(
          "position_stock_detail-options",
          JSON.stringify(this.options)
        );
      },
    },
    // 仓配中心搜索
    stockSelected: {
      handler() {
        this.options.stock_ids = this.stockSelected
          ? [this.stockSelected.id]
          : [];
        sessionStorage.setItem(
          "position_stock_detail-stockSelected",
          JSON.stringify(this.stockSelected)
        );
      },
    },
    // 库位
    positionSelected: {
      handler() {
        this.options.stock_position_ids = this.positionSelected
          ? [this.positionSelected.id]
          : [];
        sessionStorage.setItem(
          "position_stock_detail-positionSelected",
          JSON.stringify(this.positionSelected)
        );
      },
    },
    // 商品分类
    materialCategorySelected: {
      handler() {
        this.options.category_ids = this.materialCategorySelected
          ? [this.materialCategorySelected.id]
          : [];
        sessionStorage.setItem(
          "position_stock_detail-materialCategorySelected",
          JSON.stringify(this.materialCategorySelected)
        );
      },
    },
    // 商品
    materialSelected: {
      handler() {
        this.options.material_ids = this.materialSelected
          ? [this.materialSelected.id]
          : [];
        sessionStorage.setItem(
          "position_stock_detail-materialSelected",
          JSON.stringify(this.materialSelected)
        );
      },
    },
  },
};
</script>

<style>
</style>